if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = this.resolveCartUI();
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      resolveCartUI() {
        // SSSALON uses the custom cart drawer even though the global Dawn setting is "notification".
        // Always prefer the real drawer so PDP add-to-cart behaves like homepage/collection cards.
        return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      }

      getCartSections() {
        this.cart = this.resolveCartUI() || this.cart;

        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          return this.cart.getSectionsToRender();
        }

        return [
          { id: 'cart-drawer' },
          { id: 'cart-icon-bubble' },
        ];
      }

      async renderCartContents(response) {
        this.cart = this.resolveCartUI() || this.cart;

        if (!this.cart) {
          window.location = window.routes.cart_url;
          return;
        }

        const hasFreshDrawerSection = Boolean(response?.sections?.['cart-drawer']);

        if (hasFreshDrawerSection && typeof this.cart.renderContents === 'function') {
          this.cart.renderContents(response);
          return;
        }

        try {
          const sectionIds = this.getCartSections().map((section) => section.id);
          const sectionResponse = await fetch(`${routes.cart_url}?sections=${sectionIds.join(',')}`, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          });

          if (!sectionResponse.ok) throw new Error('Cart sections could not be refreshed.');

          const sections = await sectionResponse.json();
          this.cart = this.resolveCartUI() || this.cart;

          if (this.cart && typeof this.cart.renderContents === 'function') {
            this.cart.renderContents({ ...response, sections });
            return;
          }
        } catch (error) {
          console.error(error);
        }

        if (this.cart && typeof this.cart.open === 'function') this.cart.open();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.cart = this.resolveCartUI() || this.cart;
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          const sectionsToRender = this.getCartSections();
          formData.append(
            'sections',
            sectionsToRender.map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          if (typeof this.cart.setActiveElement === 'function') this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            this.cart = this.resolveCartUI() || this.cart;
            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            this.error = false;

            // The cart UI is the critical path. Render/open it immediately after Shopify
            // confirms the add, before any pub/sub subscriber can interrupt execution.
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    void this.renderCartContents(response).catch((error) => {
                      console.error(error);
                      this.cart = this.resolveCartUI() || this.cart;
                      if (this.cart && typeof this.cart.open === 'function') this.cart.open();
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              void this.renderCartContents(response).catch((error) => {
                console.error(error);
                this.cart = this.resolveCartUI() || this.cart;
                if (this.cart && typeof this.cart.open === 'function') this.cart.open();
              });
            }

            // Secondary subscribers/analytics must never block the drawer from opening.
            try {
              const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
              Promise.resolve(
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  cartData: response,
                })
              )
                .then(() => {
                  CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
                })
                .catch((error) => console.error(error));
            } catch (error) {
              console.error(error);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            this.cart = this.resolveCartUI() || this.cart;
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmit(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      // product-info.js uses Dawn's newer method name. Keep both names so variant
      // changes can update the hidden variant ID and re-enable the submit button.
      toggleSubmitButton(disable = true, text) {
        this.toggleSubmit(disable, text);
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
